import { useState, useEffect, useRef, useCallback } from "react";
import { Document, Page } from "react-pdf";
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Search,
  X,
  Loader2,
  Clock,
  Layers,
  BarChart3,
  ArrowLeft
} from "lucide-react";
import DOMPurify from "dompurify";

export function ExtractionViewer ({ onClose }) {
  // PDF State
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);

  // Document State
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [docId, setDocId] = useState("");
  const [docName, setDocName] = useState("");
  const [coordinates, setCoordinates] = useState(null);
  const [formIndex, setFormIndex] = useState(null);
  const [currentPageData, setCurrentPageData] = useState(null);

  // UI State
  const [showTables, setShowTables] = useState(true);
  const [showText, setShowText] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedText, setSelectedText] = useState(null);
  const [loading, setLoading] = useState(false);
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [autoLoadFromUrl, setAutoLoadFromUrl] = useState(false);

  // Progressive loading states
  const [coordinatesLoading, setCoordinatesLoading] = useState(false);
  const [coordinatesLoaded, setCoordinatesLoaded] = useState(false);
  const [formIndexLoading, setFormIndexLoading] = useState(false);
  const [formIndexLoaded, setFormIndexLoaded] = useState(false);

  // Search State
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);

  // Analysis State
  const [analysisContent, setAnalysisContent] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  // Force overlay re-render state
  const [overlayKey, setOverlayKey] = useState(0);

  // Refs
  const canvasRef = useRef(null);
  const searchInputRef = useRef(null);
  const pageContainerRef = useRef(null);

  // Time state for status bar
  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString()
  );

  // Check URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlDocId = urlParams.get("docId");
    const urlPdfUrl = urlParams.get("pdfUrl");
    const urlDocName = urlParams.get("docName");

    if (urlDocId && urlPdfUrl) {
      setDocId(urlDocId);
      setPdfUrl(decodeURIComponent(urlPdfUrl));
      setDocName(decodeURIComponent(urlDocName || "Document"));
      setAutoLoadFromUrl(true);
    }
  }, []);

  // Auto-load document if URL parameters are present
  useEffect(() => {
    if (autoLoadFromUrl && docId && pdfUrl) {
      loadDocumentFromUrl();
    }
  }, [autoLoadFromUrl, docId, pdfUrl]);

  // Update time every second
  // useEffect(() => {
  //   const timer = setInterval(() => {
  //     setCurrentTime(new Date().toLocaleTimeString());
  //   }, 1000);
  //   return () => clearInterval(timer);
  // }, []);

  // Remove canvas width and height attributes when canvas is rendered
  useEffect(() => {
    if (!documentLoaded) return;

    const adjustCanvasStyles = () => {
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        // Instead of removing all styles, set responsive dimensions
        canvas.style.width = "100%";
        canvas.style.height = "auto";
        canvas.style.maxWidth = "100%";
      }
    };

    // Initial adjustment
    adjustCanvasStyles();

    // Set up mutation observer to watch for style changes
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "style"
        ) {
          // Only adjust if the canvas has fixed pixel dimensions
          if (
            canvasRef.current &&
            canvasRef.current.style.width.includes("px")
          ) {
            adjustCanvasStyles();
          }
        }
      });
    });

    // Start observing the canvas for attribute changes
    if (canvasRef.current) {
      observer.observe(canvasRef.current, {
        attributes: true,
        attributeFilter: ["style"]
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [documentLoaded, currentPage, scale]);

  // Load coordinates from API
  const loadCoordinates = useCallback(async () => {
    try {
      const response = await fetch(
        `https://lexai-app-info-v2-74624883720.us-central1.run.app/pdf_text_extractor/${docId}/coordinates`
      );
      if (!response.ok) throw new Error("Failed to load coordinates");
      const data = await response.json();
      setCoordinates(data.coordinates || data);
    } catch (error) {
      console.error("Error loading coordinates:", error);
    }
  }, [docId]);

  // Load form index from API
  const loadFormIndex = useCallback(async () => {
    try {
      const response = await fetch(
        `https://lexai-app-info-v2-74624883720.us-central1.run.app/form-index/${docId}/grouped-pages`
      );
      if (!response.ok) throw new Error("Failed to load form index");
      const data = await response.json();
      setFormIndex(data);
    } catch (error) {
      console.error("Error loading form index:", error);
    }
  }, [docId]);

  // Async wrapper for loading coordinates
  const loadCoordinatesAsync = useCallback(async () => {
    setCoordinatesLoading(true);
    try {
      await loadCoordinates();
      setCoordinatesLoaded(true);
    } finally {
      setCoordinatesLoading(false);
    }
  }, [loadCoordinates]);

  // Async wrapper for loading form index
  const loadFormIndexAsync = useCallback(async () => {
    setFormIndexLoading(true);
    try {
      await loadFormIndex();
      setFormIndexLoaded(true);
    } finally {
      setFormIndexLoading(false);
    }
  }, [loadFormIndex]);

  // Load extraction data in background without blocking PDF viewer
  const loadExtractionDataInBackground = useCallback(async () => {
    // Load coordinates and form index in parallel, but don't block the UI
    Promise.all([loadCoordinatesAsync(), loadFormIndexAsync()]).catch(error => {
      console.error("Error loading extraction data:", error);
    });
  }, [loadCoordinatesAsync, loadFormIndexAsync]);

  // Load document from URL
  const loadDocumentFromUrl = useCallback(async () => {
    setLoading(true);
    setDocumentLoaded(false);

    try {
      // Fetch PDF from URL first
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      const file = new File([blob], docName, { type: "application/pdf" });
      setPdfFile(file);

      // Set document as loaded immediately after PDF is ready
      setDocumentLoaded(true);
      setLoading(false);

      // Load extraction data in the background (non-blocking)
      loadExtractionDataInBackground();
    } catch (error) {
      console.error("Error loading document from URL:", error);
      setLoading(false);
    }
  }, [pdfUrl, docName, loadExtractionDataInBackground]);

  // Handle PDF load success
  const handleDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // Update current page data when page or coordinates change
  useEffect(() => {
    if (coordinates?.pages) {
      const pageData = coordinates.pages.find(
        p => p.page_number === currentPage
      );
      setCurrentPageData(pageData);
    }
  }, [coordinates, currentPage]);

  // Force overlay re-render after page navigation to fix overlay visibility
  useEffect(() => {
    if (coordinatesLoaded && currentPageData && canvasRef.current) {
      // Small delay to ensure canvas has rendered properly
      const timer = setTimeout(() => {
        setOverlayKey(prev => prev + 1);
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [currentPage, coordinatesLoaded, currentPageData, scale]); // Added scale dependency

  // Show a brief notification when extraction data is loaded
  useEffect(() => {
    if (coordinatesLoaded && formIndexLoaded) {
      // Optional: You could show a toast notification here
      console.log("✅ Document extraction data loaded successfully");
    }
  }, [coordinatesLoaded, formIndexLoaded]);

  // Reset overlays when coordinates are not loaded
  useEffect(() => {
    if (!coordinatesLoaded) {
      setSelectedTable(null);
      setSelectedText(null);
      setAnalysisContent(null);
    }
  }, [coordinatesLoaded]);

  // Navigation functions with smooth transitions
  const goToPage = page => {
    if (page >= 1 && page <= numPages && page !== currentPage) {
      // Add smooth fade transition
      if (pageContainerRef.current) {
        pageContainerRef.current.style.opacity = "0.7";
        pageContainerRef.current.style.transform = "scale(0.98)";
      }

      setTimeout(() => {
        setCurrentPage(page);
        setSelectedTable(null);
        setSelectedText(null);
        clearAnalysis();

        // Reset PDF viewer scroll position
        if (pageContainerRef.current) {
          pageContainerRef.current.scrollTop = 0;
          pageContainerRef.current.scrollLeft = 0;

          // Restore smooth transition
          setTimeout(() => {
            pageContainerRef.current.style.opacity = "1";
            pageContainerRef.current.style.transform = "scale(1)";
          }, 100);
        }
      }, 150);
    }
  };

  const previousPage = () => goToPage(currentPage - 1);
  const nextPage = () => goToPage(currentPage + 1);

  // Zoom functions
  const zoomIn = () => setScale(prev => Math.min(prev + 0.25, 3.0));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));

  // Toggle overlays
  const toggleTables = () => setShowTables(prev => !prev);
  const toggleText = () => setShowText(prev => !prev);

  const goToSearchResult = (index, results = searchResults) => {
    if (index < 0 || index >= results.length) return;

    const result = results[index];
    setCurrentSearchIndex(index);

    if (currentPage !== result.page) {
      goToPage(result.page);
    }

    // Ensure text overlays are shown
    if (!showText) {
      setShowText(true);
    }
  };

  const previousSearchResult = () => {
    if (currentSearchIndex > 0) {
      goToSearchResult(currentSearchIndex - 1);
    }
  };

  const nextSearchResult = () => {
    if (currentSearchIndex < searchResults.length - 1) {
      goToSearchResult(currentSearchIndex + 1);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setCurrentSearchIndex(-1);
  };

  // Handle search input
  useEffect(() => {
    const performSearchInternal = query => {
      // Only perform search if coordinates are loaded
      if (!coordinatesLoaded || !coordinates?.pages || query.length < 2) {
        setSearchResults([]);
        setCurrentSearchIndex(-1);
        return;
      }

      const results = [];
      const lowerQuery = query.toLowerCase();

      coordinates.pages.forEach(page => {
        if (page.elements?.text) {
          page.elements.text.forEach((text, index) => {
            if (text.content.toLowerCase().includes(lowerQuery)) {
              results.push({
                page: page.page_number,
                textIndex: index,
                content: text.content
              });
            }
          });
        }
      });

      setSearchResults(results);
      setCurrentSearchIndex(results.length > 0 ? 0 : -1);

      // Navigate to first result
      if (results.length > 0) {
        const result = results[0];
        setCurrentSearchIndex(0);

        if (currentPage !== result.page) {
          setCurrentPage(result.page);
        }

        // Ensure text overlays are shown
        if (!showText) {
          setShowText(true);
        }
      }
    };

    if (searchQuery.trim() && coordinatesLoaded) {
      performSearchInternal(searchQuery.trim());
    } else {
      setSearchResults([]);
      setCurrentSearchIndex(-1);
    }
  }, [searchQuery, coordinates, coordinatesLoaded, currentPage, showText]);

  // Table selection
  const selectTable = async tableIndex => {
    setSelectedTable(tableIndex);
    setSelectedText(null);
    setAnalysisLoading(true);

    try {
      const apiTableIndex = tableIndex + 1; // API expects 1-based index
      const response = await fetch(
        `https://lexai-app-info-v2-74624883720.us-central1.run.app/extraction_summary/extraction_summary/${docId}/${currentPage}/${apiTableIndex}`
      );

      if (!response.ok) throw new Error("Failed to load explanation");

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const content = doc.querySelector(".interpretation");

      setAnalysisContent({
        type: "table",
        index: tableIndex + 1,
        content: content ? content.innerHTML : html
      });
    } catch (error) {
      console.error("Error loading explanation:", error);
      setAnalysisContent({
        type: "error",
        content: "Failed to load table analysis. Please try again."
      });
    } finally {
      setAnalysisLoading(false);
    }
  };

  // Text selection
  const selectText = (textIndex, textData) => {
    setSelectedText(textIndex);
    setSelectedTable(null);

    setAnalysisContent({
      type: "text",
      content: `
        <div class="space-y-4">
          <h3 class="text-lg font-semibold text-gray-900">Text Content</h3>
          <div class="bg-gray-50 p-4 rounded-lg border-2 border-gray-200 font-mono text-sm">
            ${textData.content}
          </div>
          <h4 class="text-md font-semibold text-gray-700 border-b-2 border-gray-200 pb-2">Properties</h4>
          <ul class="space-y-2 text-sm">
            <li class="flex"><span class="font-medium text-orange-600 mr-2">→</span><strong>Font:</strong> ${
              textData.properties.font
            }</li>
            <li class="flex"><span class="font-medium text-orange-600 mr-2">→</span><strong>Font Size:</strong> ${
              textData.properties.font_size
            }pt</li>
            <li class="flex"><span class="font-medium text-orange-600 mr-2">→</span><strong>Position:</strong> X: ${textData.coordinates.x.toFixed(
              2
            )}, Y: ${textData.coordinates.y.toFixed(2)}</li>
            <li class="flex"><span class="font-medium text-orange-600 mr-2">→</span><strong>Dimensions:</strong> ${textData.coordinates.width.toFixed(
              2
            )} × ${textData.coordinates.height.toFixed(2)}</li>
          </ul>
        </div>
      `
    });
  };

  const clearAnalysis = () => {
    setAnalysisContent(null);
    setSelectedTable(null);
    setSelectedText(null);
  };

  // Render overlays with proper coordinate mapping
  const renderTableOverlays = () => {
    // Only show overlays if coordinates are loaded and tables toggle is on
    if (!showTables || !coordinatesLoaded || !currentPageData?.elements?.tables)
      return null;

    const canvas = canvasRef.current;
    if (!canvas) return null;

    // Get the actual rendered dimensions of the canvas element
    const canvasRect = canvas.getBoundingClientRect();

    // Ensure canvas has valid dimensions before rendering overlays
    if (canvasRect.width === 0 || canvasRect.height === 0) return null;

    // Calculate scale based on rendered size vs PDF page dimensions
    const scaleX = canvasRect.width / currentPageData.dimensions.width;
    const scaleY = canvasRect.height / currentPageData.dimensions.height;

    return currentPageData.elements.tables.map((table, index) => (
      <div
        key={`${overlayKey}-table-${index}`} // Use overlayKey to force re-render
        className={`absolute border-3 cursor-pointer transition-all duration-300 rounded-lg ${
          selectedTable === index
            ? "border-purple-500 bg-purple-500 bg-opacity-30 shadow-lg shadow-purple-500/30 animate-pulse"
            : "border-orange-500 bg-orange-500 bg-opacity-10 hover:bg-opacity-20 hover:border-orange-600 hover:shadow-lg hover:shadow-orange-500/20 hover:scale-105"
        }`}
        style={{
          left: `${table.coordinates.x * scaleX}px`,
          top: `${table.coordinates.y * scaleY}px`,
          width: `${table.coordinates.width * scaleX}px`,
          height: `${table.coordinates.height * scaleY}px`
        }}
        onClick={() => selectTable(index)}
      >
        <div className="absolute -top-8 -left-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-1 rounded-t-lg rounded-br-lg text-xs font-bold shadow-lg">
          Table {index + 1}
        </div>
      </div>
    ));
  };

  const renderTextOverlays = () => {
    // Only show overlays if coordinates are loaded and text toggle is on
    if (!showText || !coordinatesLoaded || !currentPageData?.elements?.text)
      return null;

    const canvas = canvasRef.current;
    if (!canvas) return null;

    // Get the actual rendered dimensions of the canvas element
    const canvasRect = canvas.getBoundingClientRect();

    // Ensure canvas has valid dimensions before rendering overlays
    if (canvasRect.width === 0 || canvasRect.height === 0) return null;

    // Calculate scale based on rendered size vs PDF page dimensions
    const scaleX = canvasRect.width / currentPageData.dimensions.width;
    const scaleY = canvasRect.height / currentPageData.dimensions.height;

    return currentPageData.elements.text.map((text, index) => {
      const isSelected = selectedText === index;
      const isSearchMatch = searchResults.some(
        r => r.page === currentPage && r.textIndex === index
      );
      const isCurrentSearchResult =
        searchResults[currentSearchIndex]?.page === currentPage &&
        searchResults[currentSearchIndex]?.textIndex === index;

      return (
        <div
          key={`${overlayKey}-text-${index}`} // Use overlayKey to force re-render
          className={`absolute cursor-pointer transition-all duration-200 ${
            isCurrentSearchResult
              ? "bg-red-400 bg-opacity-50 border border-red-500 shadow-lg shadow-red-500/30 animate-pulse scale-105 z-30"
              : isSearchMatch
              ? "bg-orange-300 bg-opacity-40 border border-orange-400 shadow-lg shadow-orange-400/30 z-20"
              : isSelected
              ? "bg-green-400 bg-opacity-30 border border-green-500 shadow-lg shadow-green-500/30 z-10"
              : "bg-yellow-400 bg-opacity-30 hover:bg-opacity-45 hover:border hover:border-yellow-500 hover:shadow-lg hover:shadow-yellow-500/30 hover:z-10"
          }`}
          style={{
            left: `${text.coordinates.x * scaleX}px`,
            top: `${text.coordinates.y * scaleY}px`,
            width: `${text.coordinates.width * scaleX}px`,
            height: `${text.coordinates.height * scaleY}px`
          }}
          onClick={() => selectText(index, text)}
          title={text.content}
        />
      );
    });
  };

  // Get page info for index
  const getPageInfo = pageNumber => {
    // Return loading state or actual data based on what's available
    if (!coordinatesLoaded || !coordinates?.pages) {
      return {
        tableCount: coordinatesLoading ? "..." : 0,
        textCount: coordinatesLoading ? "..." : 0,
        forms: formIndexLoading ? ["Loading..."] : []
      };
    }

    const pageData = coordinates.pages.find(p => p.page_number === pageNumber);
    const tableCount = pageData?.elements?.tables?.length || 0;
    const textCount = pageData?.elements?.text?.length || 0;

    const forms = [];
    if (formIndexLoaded && formIndex) {
      Object.entries(formIndex).forEach(([formName, pages]) => {
        if (pages.includes(pageNumber)) {
          forms.push(formName);
        }
      });
    } else if (formIndexLoading) {
      forms.push("Loading forms...");
    }

    return { tableCount, textCount, forms };
  };

  // Handle close - for standalone page, close the tab
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      window.close();
    }
  };

  return (
    <>
      <style>
        {`
          .pdf-page-container canvas {
            width: 100% !important;
            height: auto !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            display: block !important;
          }
          .react-pdf__Page__canvas {
            width: 100% !important;
            height: auto !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            display: block !important;
          }
          .react-pdf__Document {
            margin: 0 !important;
            padding: 0 !important;
          }
          .react-pdf__Page {
            margin: 0 !important;
            padding: 0 !important;
          }
        `}
      </style>
      <div className="flex flex-col h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-orange-400 text-white p-6 shadow-lg relative overflow-hidden">
          <div className="absolute inset-0 bg-white bg-opacity-10 animate-pulse"></div>
          <div className="relative z-10 max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-8">
              <h2 className="text-xl font-bold mb-1">
                Document Extraction Viewer
              </h2>

              {/* Controls moved from left panel */}
              {documentLoaded && (
                <div className="flex items-center gap-6">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-orange-300 w-4 h-4" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder={
                        coordinatesLoaded
                          ? "Search document..."
                          : "Loading extraction data..."
                      }
                      disabled={!coordinatesLoaded}
                      className={`w-64 pl-10 pr-10 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg text-white placeholder-orange-200 focus:bg-opacity-30 focus:outline-none text-sm transition-all duration-300 ${
                        !coordinatesLoaded
                          ? "opacity-50 cursor-not-allowed"
                          : ""
                      }`}
                    />
                    {coordinatesLoading && (
                      <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-orange-200 animate-spin" />
                    )}
                    {searchQuery && coordinatesLoaded && (
                      <button
                        onClick={clearSearch}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-orange-200 hover:text-white hover:bg-white hover:bg-opacity-20 p-1 rounded-full transition-all duration-200"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>

                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-orange-200 text-sm">
                        {searchResults.length} results
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={previousSearchResult}
                          disabled={
                            searchResults.length === 0 ||
                            currentSearchIndex <= 0
                          }
                          className="p-1 bg-white bg-opacity-20 hover:bg-opacity-30 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={nextSearchResult}
                          disabled={
                            searchResults.length === 0 ||
                            currentSearchIndex >= searchResults.length - 1
                          }
                          className="p-1 bg-white bg-opacity-20 hover:bg-opacity-30 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Display Options */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-white-900 text-sm">
                        <b>Tables Overlay</b>
                        {coordinatesLoading && (
                          <Loader2 className="inline w-3 h-3 ml-1 animate-spin" />
                        )}
                      </span>
                      <button
                        onClick={coordinatesLoaded ? toggleTables : undefined}
                        disabled={!coordinatesLoaded}
                        className={`relative w-12 h-6 rounded-full transition-all duration-300 shadow-inner ${
                          !coordinatesLoaded
                            ? "bg-gray-300 shadow-gray-400 opacity-50 cursor-not-allowed"
                            : showTables
                            ? "bg-green-500 shadow-green-600"
                            : "bg-gray-400 shadow-gray-500"
                        }`}
                      >
                        <div
                          className={`absolute w-4 h-4 bg-white rounded-full transition-all duration-300 top-1 shadow-lg ${
                            showTables && coordinatesLoaded
                              ? "left-7"
                              : "left-1"
                          }`}
                        ></div>
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-white-900 text-sm">
                        <b>Text Overlay</b>
                        {coordinatesLoading && (
                          <Loader2 className="inline w-3 h-3 ml-1 animate-spin" />
                        )}
                      </span>
                      <button
                        onClick={coordinatesLoaded ? toggleText : undefined}
                        disabled={!coordinatesLoaded}
                        className={`relative w-12 h-6 rounded-full transition-all duration-300 shadow-inner ${
                          !coordinatesLoaded
                            ? "bg-gray-300 shadow-gray-400 opacity-50 cursor-not-allowed"
                            : showText
                            ? "bg-green-500 shadow-green-600"
                            : "bg-gray-400 shadow-gray-500"
                        }`}
                      >
                        <div
                          className={`absolute w-4 h-4 bg-white rounded-full transition-all duration-300 top-1 shadow-lg ${
                            showText && coordinatesLoaded ? "left-7" : "left-1"
                          }`}
                        ></div>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {(documentLoaded || autoLoadFromUrl) && (
              <button
                onClick={handleClose}
                className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-xl transition-all duration-200"
              >
                <ArrowLeft className="w-5 h-5" />
                Close
              </button>
            )}
          </div>
        </div>

        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md mx-4 text-center">
              <div className="relative mb-6">
                <div className="w-16 h-16 border-4 border-orange-200 rounded-full animate-spin mx-auto"></div>
                <div className="absolute inset-0 w-16 h-16 border-4 border-orange-500 rounded-full animate-spin mx-auto border-t-transparent"></div>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                Loading PDF Document
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Fetching and preparing the PDF document for viewing. The
                extraction analysis will load in the background once the PDF is
                ready.
              </p>
              <div className="mt-4 text-sm text-orange-600 font-medium">
                Please wait...
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        {documentLoaded && pdfFile && (
          <div className="flex-1 flex max-w-full mx-auto p-6 gap-6 overflow-hidden">
            {/* Left Panel - Page Index */}
            <div className="w-80 bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
              <div className="bg-gradient-to-r from-orange-600 to-orange-400 text-white p-6">
                <h2 className="text-xl font-bold mb-1">Document Navigator</h2>
                <p className="text-white-700 text-sm">Browse document pages</p>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {Array.from({ length: numPages }, (_, i) => i + 1).map(
                  pageNumber => {
                    const { tableCount, textCount, forms } =
                      getPageInfo(pageNumber);
                    const isActive = pageNumber === currentPage;

                    return (
                      <div
                        key={pageNumber}
                        onClick={() => goToPage(pageNumber)}
                        className={`p-4 rounded-xl cursor-pointer transition-all duration-300 border-2 relative overflow-hidden ${
                          isActive
                            ? "bg-gradient-to-r from-orange-600 to-orange-400 text-white border-transparent shadow-lg"
                            : "bg-gray-50 hover:bg-white border-gray-200 hover:border-orange-500 hover:shadow-md hover:translate-x-1"
                        }`}
                      >
                        {!isActive && (
                          <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-orange-600 to-orange-400 transform -translate-x-full transition-transform duration-300 group-hover:translate-x-0"></div>
                        )}
                        <div className="flex justify-between items-center mb-2">
                          <div className="font-bold text-lg">
                            Page {pageNumber}
                          </div>
                          <div className="flex gap-2">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                isActive
                                  ? "bg-white bg-opacity-20 text-white"
                                  : "bg-gray-400 text-black-600"
                              }`}
                            >
                              {tableCount} tables
                            </span>
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                isActive
                                  ? "bg-white bg-opacity-20 text-white"
                                  : "bg-purple-100 text-orange-400"
                              }`}
                            >
                              {textCount} text
                            </span>
                          </div>
                        </div>
                        <div
                          className={`text-sm font-medium ${
                            isActive
                              ? "text-white text-opacity-90"
                              : "text-gray-600"
                          }`}
                        >
                          {forms.length > 0
                            ? forms.join(", ")
                            : "No forms detected"}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </div>

            {/* Center Panel - PDF Viewer */}
            <div className="flex-1 bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
              {/* PDF Viewer */}
              <div
                ref={pageContainerRef}
                className="flex-1 overflow-auto bg-gray-100 flex justify-center relative transition-all duration-500 ease-in-out"
              >
                <Document
                  file={pdfFile}
                  onLoadSuccess={handleDocumentLoadSuccess}
                  className="relative m-0 p-0"
                >
                  <div className="relative bg-white shadow-2xl rounded-xl overflow-hidden m-0 p-0 transition-all duration-500 ease-in-out">
                    <div className="pdf-page-container m-0 p-0">
                      <Page
                        pageNumber={currentPage}
                        scale={scale}
                        canvasRef={canvasRef}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </div>

                    {/* Overlays */}
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="relative w-full h-full">
                        <div className="pointer-events-auto">
                          {renderTableOverlays()}
                        </div>
                        <div className="pointer-events-auto">
                          {renderTextOverlays()}
                        </div>
                      </div>
                    </div>
                  </div>
                </Document>
              </div>
            </div>

            {/* Right Panel - Analysis */}
            <div className="w-96 bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
              <div className="bg-gradient-to-r from-orange-600 to-orange-400 text-white p-6">
                <h2 className="text-xl font-bold mb-1 flex items-center gap-2">
                  <Layers className="w-6 h-6" />
                  Analysis Panel
                </h2>
                <p className="text-white-700 text-sm">
                  {analysisContent?.type === "table"
                    ? `Table ${analysisContent.index} Analysis`
                    : analysisContent?.type === "text"
                    ? "Text Element Details"
                    : "Select an element to analyze"}
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {analysisLoading ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <Loader2 className="w-12 h-12 animate-spin mb-4" />
                    <span>Loading analysis...</span>
                  </div>
                ) : !coordinatesLoaded ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <Loader2 className="w-16 h-16 animate-spin mb-4 text-orange-400" />
                    <p className="text-center font-medium text-gray-600 mb-2">
                      Loading extraction data...
                    </p>
                    <p className="text-center text-sm">
                      Table and text analysis will be available once extraction
                      completes
                    </p>
                  </div>
                ) : analysisContent ? (
                  <div className="animate-fade-in">
                    {analysisContent.type === "error" ? (
                      <div className="bg-red-50 border-2 border-red-200 text-red-600 p-4 rounded-xl text-center font-medium">
                        {analysisContent.content}
                      </div>
                    ) : (
                      <div
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(analysisContent.content)
                        }}
                      />
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <BarChart3 className="w-24 h-24 mb-6 opacity-20" />
                    <p className="text-center">
                      Select a table or text element to view analysis
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Status/Control Bar */}
        {documentLoaded && (
          <div className="bg-white bg-opacity-95 backdrop-blur border-t shadow-lg p-4">
            <div className="max-w-7xl mx-auto flex justify-between items-center text-sm text-gray-600">
              {/* Left side - Document info */}
              <div className="flex gap-8">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full shadow-lg shadow-green-500/30 animate-pulse"></div>
                  <span className="font-medium">PDF loaded</span>
                </div>

                {/* Extraction status indicators */}
                <div className="flex items-center gap-2">
                  {coordinatesLoading ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin text-orange-500" />
                      <span className="font-medium text-orange-600">
                        Loading extraction data...
                      </span>
                    </>
                  ) : coordinatesLoaded ? (
                    <>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="font-medium">
                        {currentPageData?.elements?.tables?.length || 0} tables,{" "}
                        {currentPageData?.elements?.text?.length || 0} text
                        elements
                      </span>
                    </>
                  ) : (
                    <span className="font-medium text-gray-500">
                      No extraction data
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {formIndexLoading ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin text-blue-500" />
                      <span className="font-medium text-blue-600">
                        Loading forms...
                      </span>
                    </>
                  ) : formIndexLoaded ? (
                    <>
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span className="font-medium">Forms indexed</span>
                    </>
                  ) : (
                    <span className="font-medium text-gray-500">
                      Forms pending
                    </span>
                  )}
                </div>

                <div className="font-medium">{numPages} pages total</div>
              </div>

              {/* Center - Navigation Controls */}
              <div className="flex items-center gap-4">
                <button
                  onClick={previousPage}
                  disabled={currentPage <= 1}
                  className="p-3 bg-gradient-to-r from-orange-600 to-orange-400 text-white rounded-xl disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-lg hover:scale-105 disabled:hover:scale-100"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="px-4 py-2 bg-white border-2 border-gray-200 rounded-full font-semibold text-gray-700 min-w-32 text-center">
                  Page {currentPage} of {numPages}
                </div>
                <button
                  onClick={nextPage}
                  disabled={currentPage >= numPages}
                  className="p-3 bg-gradient-to-r from-orange-600 to-orange-400 text-white rounded-xl disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-lg hover:scale-105 disabled:hover:scale-100"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Right side - Zoom and Time */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 bg-white border-2 border-gray-200 rounded-full p-1">
                  <button
                    onClick={zoomOut}
                    className="p-2 hover:bg-gray-100 rounded-full transition-all duration-200 hover:scale-110"
                  >
                    <ZoomOut className="w-5 h-5 text-gray-600" />
                  </button>
                  <div className="min-w-16 text-center font-semibold text-gray-700 text-sm">
                    {Math.round(scale * 100)}%
                  </div>
                  <button
                    onClick={zoomIn}
                    className="p-2 hover:bg-gray-100 rounded-full transition-all duration-200 hover:scale-110"
                  >
                    <ZoomIn className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
                {/* <div className="flex items-center gap-2 font-medium">
                  <Clock className="w-4 h-4" />
                  {currentTime}
                </div> */}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
